import { tw } from "@careinfo/tailwind";
import React, { MouseEvent, useEffect, useRef, useState } from "react";
import { Control, useController } from "react-hook-form";

interface Props {
  name: string;
  control: Control;
}

export default function MultiImageField({ name, control }: Props) {
  const {
    field: { value = null, onChange },
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: null,
  });

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (value instanceof File) {
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result as string);
      reader.readAsDataURL(value);
    } else if (typeof value === "string") {
      setPreviewUrl(value);
    } else {
      setPreviewUrl(null);
    }
  }, [value]);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) handleFileSelection(files[0]);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) handleFileSelection(files[0]);
  };

  const handleFileSelection = (file: File) => {
    if (file && file.type.startsWith("image/")) {
      setIsUploading(true);
      setTimeout(() => {
        onChange(file);
        setIsUploading(false);
      }, 1000);
    }
  };

  const handleImageClear = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setPreviewUrl(null);
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={tw` w-full`}>
      <p
        className={tw`text-[16px] font-[500] leading-[22px] text-left text-[#030303] pb-2`}
      >
        Media
      </p>
      <div
        className={tw`input_field w-full h-[180px] p-[20px] gap-[10px] border border-[#00000033] rounded-[4px] flex flex-col justify-center items-center cursor-pointer relative`}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        onClick={() => fileInputRef.current?.click()}
        role="button"
        tabIndex={-1}
      >
        <input
          type="file"
          ref={fileInputRef}
          className={tw`hidden`}
          accept="image/*"
          onChange={handleImageChange}
        />
        {previewUrl ? (
          isUploading ? (
            <p className={tw`animate-pulse text-[#888]`}>Uploading...</p>
          ) : (
            <>
              <img
                src={previewUrl}
                alt="Preview"
                className={tw`object-contain max-h-[170px]`}
              />
              <button
                className={tw`text-xs bg-primary flex items-center justify-center text-white w-8 h-8 aspect-square rounded-full absolute top-0 right-0`}
                type="button"
                onClick={handleImageClear}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18M6 6L18 18"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </>
          )
        ) : (
          <p className={tw`text-center text-[#CCCCCC]`}>
            Drop Here to Upload and Preview
          </p>
        )}
      </div>
      {error && (
        <div className={tw`text-red-600 text-[12px]`}>{error.message}</div>
      )}
    </div>
  );
}
